import { useState, useEffect, useRef } from 'react';
import { X, Save, Loader2, ImagePlus, Trash2, AlertTriangle } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { RichTextEditor, uploadBlogImage } from './RichTextEditor';

type Status = 'draft' | 'scheduled' | 'published';

type BlogPostFull = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  contentHtml: string;
  featuredImageUrl: string | null;
  featuredImageAlt: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  targetKeyword: string;
  searchIntent: string;
  category: string;
  tags: string[];
  internalLinks: Array<{ anchor: string; url: string }>;
  cta: string;
  status: Status;
  scheduledAt: string | null;
};

type Form = {
  title: string;
  slug: string;
  excerpt: string;
  contentHtml: string;
  featuredImageUrl: string | null;
  featuredImageAlt: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  targetKeyword: string;
  searchIntent: string;
  category: string;
  tagsInput: string;
  cta: string;
  status: Status;
  scheduledAtLocal: string;
};

const BLANK: Form = {
  title: '', slug: '', excerpt: '', contentHtml: '', featuredImageUrl: null, featuredImageAlt: '',
  metaTitle: '', metaDescription: '', metaKeywords: '', targetKeyword: '', searchIntent: '',
  category: '', tagsInput: '', cta: '', status: 'draft', scheduledAtLocal: '',
};

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Create / edit modal for a blog article.
 * Closes ONLY via the X button (no backdrop click, no ESC) so the admin can
 * never lose a draft to an accidental misclick.
 */
export function BlogPostModal({
  open,
  postId,
  onClose,
  onSaved,
}: {
  open: boolean;
  postId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm]       = useState<Form>(BLANK);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [dirty, setDirty]     = useState(false);
  const internalLinks         = useRef<Array<{ anchor: string; url: string }>>([]);
  const fileInput             = useRef<HTMLInputElement>(null);

  // Load (edit) or reset (new) whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setDirty(false);
    internalLinks.current = [];
    if (!postId) {
      setForm(BLANK);
      return;
    }
    setLoading(true);
    api<{ post: BlogPostFull }>(`/api/admin/blog/${postId}`)
      .then(({ post }) => {
        internalLinks.current = post.internalLinks ?? [];
        setForm({
          title: post.title, slug: post.slug, excerpt: post.excerpt, contentHtml: post.contentHtml,
          featuredImageUrl: post.featuredImageUrl, featuredImageAlt: post.featuredImageAlt,
          metaTitle: post.metaTitle, metaDescription: post.metaDescription, metaKeywords: post.metaKeywords,
          targetKeyword: post.targetKeyword, searchIntent: post.searchIntent, category: post.category,
          tagsInput: post.tags.join(', '), cta: post.cta, status: post.status,
          scheduledAtLocal: toLocalInput(post.scheduledAt),
        });
      })
      .catch(() => setError('No se pudo cargar el artículo.'))
      .finally(() => setLoading(false));
  }, [open, postId]);

  // Lock body scroll while the modal is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  function set<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  }

  function requestClose() {
    if (dirty && !window.confirm('¿Cerrar sin guardar? Se perderán los cambios.')) return;
    onClose();
  }

  async function handleImageFile(file: File) {
    setUploading(true);
    setError(null);
    try {
      const url = await uploadBlogImage(file);
      set('featuredImageUrl', url);
    } catch {
      setError('No se pudo subir la imagen destacada.');
    } finally {
      setUploading(false);
    }
  }

  async function save() {
    if (!form.title.trim()) {
      setError('El título es obligatorio.');
      return;
    }
    if (form.status === 'scheduled' && !form.scheduledAtLocal) {
      setError('Indica la fecha de publicación para un artículo programado.');
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      excerpt: form.excerpt,
      contentHtml: form.contentHtml,
      featuredImageUrl: form.featuredImageUrl || undefined,
      featuredImageAlt: form.featuredImageAlt,
      metaTitle: form.metaTitle,
      metaDescription: form.metaDescription,
      metaKeywords: form.metaKeywords,
      targetKeyword: form.targetKeyword,
      searchIntent: form.searchIntent,
      category: form.category,
      tags: form.tagsInput.split(',').map((t) => t.trim()).filter(Boolean),
      internalLinks: internalLinks.current,
      cta: form.cta,
      status: form.status,
      scheduledAt:
        form.status === 'scheduled' && form.scheduledAtLocal
          ? new Date(form.scheduledAtLocal).toISOString()
          : null,
    };
    try {
      if (postId) {
        await api(`/api/admin/blog/${postId}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await api('/api/admin/blog', { method: 'POST', body: JSON.stringify(payload) });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo guardar el artículo.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 p-4 overflow-y-auto">
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleImageFile(f);
          e.target.value = '';
        }}
      />
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl my-4">
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-slate-900">
            {postId ? 'Editar artículo' : 'Nuevo artículo'}
          </h2>
          <button
            onClick={requestClose}
            title="Cerrar"
            className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-16 flex items-center justify-center gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {error && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2">
                <AlertTriangle size={15} /> {error}
              </p>
            )}

            {/* featured image */}
            <div>
              <label className="label">Imagen destacada</label>
              {form.featuredImageUrl ? (
                <div className="relative group">
                  <img
                    src={form.featuredImageUrl}
                    alt={form.featuredImageAlt}
                    className="w-full h-52 object-cover rounded-xl border border-slate-200"
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={() => fileInput.current?.click()}
                      className="bg-white/90 backdrop-blur text-slate-700 text-xs font-medium rounded-lg px-2.5 py-1.5 hover:bg-white"
                    >
                      Cambiar
                    </button>
                    <button
                      onClick={() => set('featuredImageUrl', null)}
                      className="bg-white/90 backdrop-blur text-red-600 rounded-lg p-1.5 hover:bg-white"
                      title="Quitar"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => fileInput.current?.click()}
                  disabled={uploading}
                  className="w-full h-40 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-brand-400 hover:text-brand-500 transition"
                >
                  {uploading ? <Loader2 size={22} className="animate-spin" /> : <ImagePlus size={22} />}
                  <span className="text-sm font-medium">
                    {uploading ? 'Subiendo…' : 'Subir imagen destacada'}
                  </span>
                </button>
              )}
              <input
                value={form.featuredImageAlt}
                onChange={(e) => set('featuredImageAlt', e.target.value)}
                placeholder="Texto alternativo (alt) de la imagen — importante para SEO"
                className="input mt-2"
              />
            </div>

            {/* title */}
            <div>
              <label className="label">Título</label>
              <input
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Título del artículo"
                className="input"
              />
            </div>

            {/* slug */}
            <div>
              <label className="label">Slug (URL)</label>
              <input
                value={form.slug}
                onChange={(e) => set('slug', e.target.value)}
                placeholder="se-genera-del-titulo-si-lo-dejas-vacio"
                className="input font-mono text-xs"
              />
            </div>

            {/* excerpt */}
            <div>
              <label className="label">Resumen (se usa para la metadata SEO)</label>
              <textarea
                value={form.excerpt}
                onChange={(e) => set('excerpt', e.target.value)}
                rows={2}
                placeholder="Resumen corto del artículo"
                className="input resize-y"
              />
            </div>

            {/* content */}
            <div>
              <label className="label">Contenido</label>
              <RichTextEditor value={form.contentHtml} onChange={(html) => set('contentHtml', html)} />
            </div>

            {/* SEO block */}
            <div className="border border-slate-200 rounded-xl p-4 space-y-4 bg-slate-50/60">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400">
                SEO
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">Meta título</label>
                  <input value={form.metaTitle} onChange={(e) => set('metaTitle', e.target.value)} maxLength={200} placeholder="Máx. 60 caracteres recomendado" className="input" />
                </div>
                <div>
                  <label className="label">Categoría</label>
                  <input value={form.category} onChange={(e) => set('category', e.target.value)} className="input" />
                </div>
              </div>
              <div>
                <label className="label">Meta descripción</label>
                <textarea value={form.metaDescription} onChange={(e) => set('metaDescription', e.target.value)} rows={2} maxLength={320} placeholder="Máx. 155 caracteres recomendado" className="input resize-y" />
              </div>
              <div>
                <label className="label">Meta tags / palabras clave</label>
                <input value={form.metaKeywords} onChange={(e) => set('metaKeywords', e.target.value)} placeholder="palabra clave 1, palabra clave 2, …" className="input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">Tags del artículo</label>
                  <input value={form.tagsInput} onChange={(e) => set('tagsInput', e.target.value)} placeholder="tag1, tag2, tag3" className="input" />
                </div>
                <div>
                  <label className="label">Keyword principal</label>
                  <input value={form.targetKeyword} onChange={(e) => set('targetKeyword', e.target.value)} className="input" />
                </div>
              </div>
            </div>

            {/* publish controls */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label">Estado</label>
                <select
                  value={form.status}
                  onChange={(e) => set('status', e.target.value as Status)}
                  className="input"
                >
                  <option value="draft">Borrador</option>
                  <option value="scheduled">Programado</option>
                  <option value="published">Publicado</option>
                </select>
              </div>
              {form.status === 'scheduled' && (
                <div>
                  <label className="label">Fecha de publicación</label>
                  <input
                    type="datetime-local"
                    value={form.scheduledAtLocal}
                    onChange={(e) => set('scheduledAtLocal', e.target.value)}
                    className="input"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* footer — close only via the X above, never via backdrop/ESC */}
        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-slate-200 sticky bottom-0 bg-white rounded-b-2xl">
          <span className="text-xs text-slate-400">
            Para cerrar usa la X de arriba.
          </span>
          <button onClick={save} disabled={saving || loading} className="btn-primary gap-2">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
